import React, { ChangeEvent, FC, useEffect } from 'react';

import useIsMobile from '../../../../hooks/useIsMobile';
import useToast from '../../../../hooks/useToast';
import translate from '../../../../i18n/translate';
import SearchField from '../../SearchField';

import SearchList from './SearchList';
import {
  getIsEmpty,
  getIsSearching,
  getShouldShowNoResults,
  searchDataByTitle,
} from './helpers';
import * as Styled from './styled';
import { DataType } from './types';

type Props = {
  ariaDescribedBy: string;
  ariaLabelledBy: string;
  dataSearchValue: string;
  dataAll: DataType[];
  dataCurrent: DataType['id'] | null;
  isLoading: boolean;
  onSearch: (term: string) => void;
  requestDataSelect: (dataId: DataType['id']) => void;
  shouldPushToast?: boolean;
  type: 'template' | 'topic';
};

/**
 * Search and select template/topic for compose modal
 *
 * @param props                   Props passed to the component
 * @param props.ariaDescribedBy   Accessible form description
 * @param props.ariaLabelledBy    Accessible form title
 * @param props.dataAll           All data
 * @param props.dataCurrent       Currently selected data
 * @param props.dataSearchValue   <input type="search" /> value attribute
 * @param props.isLoading         Whether the data is still being loaded
 * @param props.onSearch          Callback to invoke when search term is changed
 * @param props.requestDataSelect Request selection of the data
 * @param props.shouldPushToast   Indicates whether the toastMessage should be pushed
 * @param props.type              Type of the data (template or topic)
 * @returns                       The component itself
 */
const TopicTemplateSelection: FC<Props> = ({
  ariaDescribedBy,
  ariaLabelledBy,
  dataAll,
  dataCurrent,
  dataSearchValue,
  isLoading,
  onSearch,
  requestDataSelect,
  shouldPushToast,
  type,
}) => {
  const dataVisible = searchDataByTitle(dataAll, dataSearchValue);
  const isMobile = useIsMobile();

  const isEmpty = getIsEmpty(dataVisible);
  const isSearching = getIsSearching(dataSearchValue);
  const isTemplate = type === 'template';

  const emptyText = translate('TEMPLATES__EMPTY_MESSAGE');
  const searchLabel = translate(
    isTemplate ? 'SEARCH__TEMPLATES' : 'TEMPLATE__TOPICS__SEARCH_LABEL',
  );
  const pushToast = useToast();

  const shouldShowNoResults = getShouldShowNoResults({
    isEmpty,
    isLoading,
    isSearching,
  });

  useEffect(() => {
    if (shouldPushToast) {
      pushToast({
        text: translate('TEMPLATE__SELECT_WARNING'),
        type: 'warning',
      });
    }
  }, [shouldPushToast]);

  if (shouldShowNoResults) {
    return (
      <Styled.Wrapper data-is-empty={true}>
        <Styled.EmptyText>{emptyText}</Styled.EmptyText>
      </Styled.Wrapper>
    );
  }

  /**
   * Handles search input change event
   *
   * @param event Event that took place
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onSearch(event.currentTarget.value);
  };

  return (
    <Styled.Wrapper
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
    >
      <header aria-label={searchLabel}>
        <SearchField
          autoFocus={!isMobile}
          onChange={handleSearch}
          placeholder={searchLabel}
          value={dataSearchValue}
        />
      </header>
      <SearchList
        dataSelected={dataCurrent}
        dataVisible={dataVisible}
        isLoading={isLoading}
        isSearching={isSearching}
        requestDataSelect={requestDataSelect}
        type={type}
      />
    </Styled.Wrapper>
  );
};

export default TopicTemplateSelection;
